import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useSelectedTaxDeclaration from "my-tax/hooks/use-selected-tax-declaration";
import useTaxApplications from "my-tax/hooks/use-tax-applications";

interface TaxCreditApplicationButtonBarsProps {
  cancelData: () => void;
  handlePrint: () => void;
  taxApplicationSaveEnabled: boolean;
  saveData?: () => void;
  editable: boolean;
  disabledButton: boolean;
  handleRemoveRequest: () => void;
  showModifiedButton?: boolean;
  showRemoveButton?: boolean;
  isModified: boolean;
  isModifiedDeclaration: boolean;
  isMobile: boolean;
}

export const TaxCreditApplicationButtonBars: React.FC<TaxCreditApplicationButtonBarsProps> = ({
  cancelData,
  handlePrint,
  taxApplicationSaveEnabled,
  saveData = () => { },
  editable,
  disabledButton,
  handleRemoveRequest,
  showModifiedButton = false,
  showRemoveButton = false,
  isModified,
  isModifiedDeclaration,
  isMobile,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { resetTaxApplications } = useTaxApplications();

  const {
    selected,
    setSelected,
    reset: resetSelectedState,
  } = useSelectedTaxDeclaration();

  const handleModifiedButtonClick = () => {
    resetTaxApplications();
    const newState = { ...selected, editable: true, isModified: true };
    setSelected(newState);
  };

  const handleCancel = () => {
    resetTaxApplications();
    cancelData();
    resetSelectedState();
  };

  const handleSaveData = () => {
    resetTaxApplications();
    saveData();
  };

  return (
    <Box>
      <Box
        style={{
          backgroundColor: theme.palette.white.main,
          padding: "10px 16px",
          display: "flex",
          justifyContent: isMobile ? "center" : "space-between",
          border: `1px solid ${theme.palette.gray40.main}`,
          margin: isMobile ? "unset" : "5px -24px -21px -24px",
          flexDirection: isMobile ? "column-reverse" : "row",
          alignItems: isMobile ? "center" : "unset",
        }}
      >
        {showModifiedButton && !editable && isMobile && <Box height="16px" />}
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="start"
          alignItems="center"
        >
          {showRemoveButton && (
            <>
              <Button
                id="remove-button"
                onClick={handleRemoveRequest}
                disableElevation
                variant="text"
                color="primary"
                style={{
                  color: theme.palette.primary.main,
                }}
              >
                {t("OC_REMOVE_REQUEST")}
              </Button>
            </>
          )}
          {showModifiedButton && !editable && (
            <>
              <Button
                id="show-modified-button"
                onClick={handleModifiedButtonClick}
                variant="text"
                color="primary"
                disableElevation
                style={{
                  color: theme.palette.primary.main,
                }}
              >
                {t("MY_TAX_MODIFIED_DECLARATION")}
              </Button>
            </>
          )}
        </Box>
        {isMobile && <Box height="16px" />}
        <Box
          display="flex"
          flexDirection={isMobile ? "column-reverse" : "row"}
          justifyContent="flex-end"
          alignItems="center"
          width={isMobile ? "100%" : "auto"}
        >
          <Button
            id="cancel-button"
            variant="text"
            color="secondary"
            onClick={handleCancel}
          >
            {t("OC_CANCEL")}
          </Button>
          {isMobile && <Box height="16px" />}
          <Box width="16px" />
          {taxApplicationSaveEnabled &&
            !isModifiedDeclaration &&
            !isModified && (
              <Button
                variant="contained"
                color="primary"
                id="save-button"
                onClick={handleSaveData}
                disabled={disabledButton}
                style={{ width: isMobile ? "100%" : "auto" }}
              >
                {t("OC_SAVE")}
              </Button>
            )}
          {(!editable ||
            isModified ||
            isModifiedDeclaration ||
            !taxApplicationSaveEnabled) && (
              <Button
                id="print-button"
                onClick={handlePrint}
                variant="contained"
                color="primary"
                disableElevation
                style={{ width: isMobile ? "100%" : "auto" }}
              >
                {t("OC_PRINT")}
              </Button>
            )}
        </Box>
      </Box>
    </Box>
  );
};

export default TaxCreditApplicationButtonBars;
